import { motion } from "motion/react";
import { Target, Eye, Building } from "lucide-react";

const values = [
  {
    icon: Target,
    title: "Our Mission",
    description:
      "To promote and sustain a viable educational foundation for our youths based on a comprehensive approach to the development of their mental, physical, cultural, moral, and spiritual values.",
    color: "from-red-500 to-red-600",
  },
  {
    icon: Eye,
    title: "Our Vision",
    description:
      "To be the School of Choice in the provision of high-quality education based on sound multi-sectorial faith teaching and strong family values.",
    color: "from-blue-600 to-blue-700",
  },
  {
    icon: Building,
    title: "Our Campus",
    description:
      "Located in Kitale, our serene hilltop campus offers a safe, nurturing, and homely environment designed to support academic excellence and holistic student growth.",
    color: "from-blue-800 to-blue-900",
  },
];

export function AboutContent() {
  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-10 w-72 h-72 bg-red-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6 border border-white/20"
          >
            Our Story
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
          >
            About Hilltop
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white/80 max-w-3xl mx-auto"
          >
            Elimu ni Ufunguo — Education is the key. At Hilltop Preparatory School,
            we nurture young minds through quality education rooted in faith,
            family values, and holistic development.
          </motion.p>
        </div>
      </section>

      {/* About Text */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Hilltop Preparatory School is committed to providing a strong and
              sustainable educational foundation for every learner. Our approach
              focuses on developing students intellectually, morally, socially,
              and spiritually to prepare them for a successful future.
            </p>
            <p className="text-xl text-slate-600 leading-relaxed">
              <span className="text-red-600 font-semibold">
                Education is our foundation.
              </span>{" "}
              Through dedication, teamwork, and professionalism, we create a
              warm and supportive environment where every child can grow,
              thrive, and reach their full potential.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission, Vision, Campus */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Our Foundation
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="group"
              >
                <div className="bg-white rounded-3xl p-8 h-full shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-100">
                  <div
                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${value.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                  >
                    <value.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Join Us?
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Discover why Hilltop Preparatory is the perfect place for your child
              to learn, grow, and thrive.
            </p>
            <a
              href="mailto:info@hilltop.co.ke?subject=Application%20Request&body=Hello%20Hilltop%20Team,%0D%0A%0D%0AI%20would%20like%20to%20apply."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-red-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-red-700 hover:shadow-xl transition-all duration-300"
            >
              Apply Now
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
